/*
 * Copyright (c) 2026 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { AddressDTO } from './address-dto.model';
import { DelAddressDtoModel } from './del-address-dto.model';
import { PointDtoModel } from './point-dto.model';

export type ExtractedAddressFields = {
  addressLine?: string | null;
  streetName?: string | null;
  streetNumber?: string | null;
  city?: string | null;
  country?: string | null;
  postalCode?: string | null;
  state?: string | null;
  lat?: number | null;
  lng?: number | null;
};

const isPresentString = (v: string | null | undefined): v is string => v != null && v !== '';

const buildPoint = (lat: number | null | undefined, lng: number | null | undefined): PointDtoModel | null => {
  if (lat == null || Number.isNaN(lat) || lng == null || Number.isNaN(lng)) return null;
  const point = new PointDtoModel();
  point.latitude = lat;
  point.longitude = lng;
  return point;
};

export const addressFromExtractedFields = (
  fields: ExtractedAddressFields | null | undefined,
): AddressDTO | null => {
  if (!fields) return null;
  const dto = new AddressDTO();
  let hasField = false;
  const setStr = <K extends keyof AddressDTO>(key: K, value: string | null | undefined) => {
    if (isPresentString(value)) {
      (dto[key] as unknown) = value;
      hasField = true;
    }
  };
  setStr('addressLine', fields.addressLine);
  setStr('streetName', fields.streetName);
  setStr('streetNumber', fields.streetNumber);
  setStr('city', fields.city);
  setStr('country', fields.country);
  setStr('postalCode', fields.postalCode);
  setStr('state', fields.state);

  const point = buildPoint(fields.lat, fields.lng);
  if (point) {
    dto.pointDTO = point;
    hasField = true;
  }

  return hasField ? dto : null;
};

export const addressFromMatrixParams = (
  params: Record<string, string | undefined>,
): AddressDTO | null => {
  const str = (key: string): string | null => {
    const v = params[key];
    return isPresentString(v) ? v : null;
  };
  const num = (key: string): number | null => {
    const v = params[key];
    if (!isPresentString(v)) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };
  return addressFromExtractedFields({
    addressLine: str('addressLine'),
    streetName: str('streetName'),
    streetNumber: str('streetNumber'),
    city: str('city'),
    country: str('country'),
    postalCode: str('postalCode'),
    state: str('state'),
    lat: num('lat'),
    lng: num('lng'),
  });
};

export const delAddressToAddressDTO = (
  src: DelAddressDtoModel | null | undefined,
): AddressDTO | null => {
  if (!src) return null;
  const dto = new AddressDTO();
  dto.addressLine = src.addressLine ?? null;
  dto.streetName = src.streetName ?? null;
  dto.streetNumber = src.streetNumber ?? null;
  dto.city = src.city ?? null;
  dto.country = src.country ?? null;
  dto.postalCode = src.postalCode ?? null;
  dto.state = src.state ?? null;
  dto.pointDTO = src.pointDTO ?? null;
  return dto;
};
